import axios from 'axios';

import Sentry from '@lib/analytics/sentry';
import { Auth } from '@lib/entities';
import { SocialProvider, SocialProviderToken } from '@lib/entities/Auth';
import { getDeviceFingerprint, getDeviceType, getPlatform } from '@lib/utils/deviceFingerprint';

import client, {
    ApiError,
    getStoredSession,
    handleAxiosError,
    PhoneInUseError,
    wrapApiCall
} from './client';

export type DietaryRestrictions = {
    soy: boolean;
    dairy: boolean;
    nuts: boolean;
    gluten: boolean;
    fish: boolean;
    meat: boolean;
};

export type SignupParams = {
    Email: string;
    Password: string;
    FName: string;
    LName: string;
    emailConsent: boolean;
    smsConsent: boolean;
    dietPreferenceIds: number[];
    socialToken?: string;
    socialProvider?: SocialProvider;
    source?: string;
    store_id?: number;
};

type SignupPayload = SignupParams & {
    Role: number;
    Status: number;
    UserId: number;
    registrationChannel: string;
    device_fingerprint?: string;
    device_type?: string;
    platform?: string;
    hct?: string;
};

const signup = async (
    params: SignupParams,
    isMfaEnabled = false,
    captchaToken?: string
): Promise<Auth | ApiError> => {
    try {
        const payload: SignupPayload = {
            ...params,
            Role: 0,
            Status: 1,
            UserId: 0,
            registrationChannel: 'App'
        };

        if (isMfaEnabled) {
            const deviceId = await getDeviceFingerprint();
            const platform = await getPlatform();
            const deviceType = getDeviceType();
            payload.device_fingerprint = deviceId;
            payload.device_type = deviceType;
            payload.platform = platform;
            if (captchaToken) {
                payload.hct = captchaToken;
            }
        }

        const endpointVersionSlug = isMfaEnabled ? 'v5' : 'v3';
        const endpointPath = isMfaEnabled ? 'Security/signup' : 'user/signup';

        const data = await client.post(`${endpointVersionSlug}/${endpointPath}`, payload);
        return data.data;
    } catch (error) {
        if (axios.isAxiosError(error)) {
            if (error.response?.data?.error) {
                return error.response?.data as ApiError;
            }
            return handleAxiosError(error);
        }
        throw error;
    }
};

const login = async (
    email: string,
    password: string,
    captchaToken?: string,
    isMfaEnabled = false
): Promise<Auth | ApiError> => {
    try {
        const payload: {
            username: string;
            password: string;
            hct?: string;
            device_fingerprint?: string;
            device_type?: string;
            platform?: string;
        } = {
            username: email,
            password
        };

        if (captchaToken) {
            payload.hct = captchaToken; // without this check, native will crash
        }

        if (isMfaEnabled) {
            const deviceId = await getDeviceFingerprint();
            const platform = await getPlatform();
            const deviceType = getDeviceType();
            payload.device_fingerprint = deviceId;
            payload.device_type = deviceType;
            payload.platform = platform;
        }

        const endpointVersionSlug = isMfaEnabled ? 'v5' : 'v4';

        const data = await client.post(`${endpointVersionSlug}/Security/Login`, payload);
        return data.data;
    } catch (error) {
        if (axios.isAxiosError(error)) {
            console.warn('login failed', { status: error.response?.status });
            if (error.response?.data?.error) {
                return error.response?.data as ApiError;
            } else if (error.response?.status === 401) {
                return { error: 'Incorrect email or password' } as ApiError;
            }
            return handleAxiosError(error);
        }
        throw error;
    } finally {
        // no-op voodoo debugging native crash on hcaptcha fail.
    }
};

const forgotPassword = async (email: string): Promise<{ email: string } | ApiError> => {
    try {
        const { data } = await client.post('/v1/user/forgot-password', { email });

        return data;
    } catch (error) {
        if (axios.isAxiosError(error)) {
            if (error.response?.data?.error) {
                return error.response?.data as ApiError;
            }
            return handleAxiosError(error);
        }
        throw error;
    }
};

interface ResetPasswordParams {
    token: string;
    password: string;
}

const resetPassword = wrapApiCall(
    async ({ token, password }: ResetPasswordParams): Promise<ResetPasswordParams | ApiError> => {
        const { data } = await client.post('/v1/user/reset-password', { token, password });
        return data;
    }
);

const refreshToken = async (refreshToken: string): Promise<Auth | ApiError> => {
    try {
        const data = await client.post('v1/security/RefreshToken', {
            RefreshToken: refreshToken
        });
        return data.data;
    } catch (error) {
        if (axios.isAxiosError(error)) {
            if (error.response?.data?.error) {
                return error.response?.data as ApiError;
            }
            return handleAxiosError(error);
        }
        throw error;
    }
};

/**
 * Exchanges an OOBO token for a JWT for the target user
 */
const exchangeOoboToken = wrapApiCall(async (ooboToken: string): Promise<Auth | ApiError> => {
    const data = await client.post(
        'v1/security/impersonate/exchange-jwt',
        {
            ooboToken
        },
        {
            headers: {
                // We assume the user isn't logged in, so we use the ooboToken,
                // which doubles as a proper JWT with additional OOBO claims
                Authorization: `Bearer ${ooboToken}`
            }
        }
    );
    return data.data;
});

/**
 * Exchanges a social provider token for a Just Salad JWT
 *
 * @remarks
 * The user must have already associated their social account with their Just Salad account.
 *
 * When `isMfaEnabled` is true the request targets the v5 endpoint and includes the device
 * fingerprint, mirroring email/password `login`/`signup`: v5 is the only version that can
 * issue the 428 device-verification challenge, and it can only challenge a device it was
 * told about. Flag off → the v1 endpoint and no device fields (unchanged behavior).
 *
 * @param socialToken The token provided by the social provider
 * @param isMfaEnabled Select the v5 endpoint + send device fields (device-verification or
 * account-verification active)
 */
const exchangeSocialProviderToken = wrapApiCall(
    async ({
        socialToken,
        fname,
        lname,
        isMfaEnabled = false
    }: {
        socialToken: SocialProviderToken;
        fname: string | undefined;
        lname: string | undefined;
        isMfaEnabled?: boolean;
    }): Promise<Auth | ApiError> => {
        const payload: {
            token: string;
            provider: SocialProvider;
            firstName?: string;
            lastName?: string;
            device_fingerprint?: string;
            device_type?: string;
            platform?: string;
        } = {
            token: socialToken.token,
            provider: socialToken.provider,
            ...(fname ? { firstName: fname } : {}),
            ...(lname ? { lastName: lname } : {})
        };

        if (isMfaEnabled) {
            payload.device_fingerprint = (await getDeviceFingerprint()) ?? undefined;
            payload.device_type = getDeviceType();
            payload.platform = await getPlatform();
        }

        const endpointVersionSlug = isMfaEnabled ? 'v5' : 'v1';

        const data = await client.post(
            `${endpointVersionSlug}/Security/social/signin-signup`,
            payload
        );

        return data.data;
    }
);

/**
 * Associates a social provider token with the already logged in user
 *
 * @param socialToken The token provided by the social provider
 */
const associateSocialProviderToken = wrapApiCall(
    async (socialToken: SocialProviderToken): Promise<Auth | ApiError> => {
        const data = await client.post('v2/user/social/associate', {
            token: socialToken.token,
            provider: socialToken.provider
        });
        return data.data;
    }
);

export type VerifyMfaParams = {
    code: string;
    device_fingerprint: string;
    device_type: string;
    platform: string;
    trust_device: boolean;
    hct?: string;
};

export type VerifyMfaResponse = {
    /** Backend sends `true` on the sole success path; every failure is a 4xx with `error`. */
    success?: boolean;
    error?: string;
};

const verifyMfa = async (
    params: VerifyMfaParams,
    authToken: string
): Promise<VerifyMfaResponse | ApiError> => {
    try {
        const { data } = await client.post('v5/Security/verify-mfa', params, {
            headers: { Authorization: `Bearer ${authToken}` }
        });

        // Require the positive signal rather than inferring success from the absence of an
        // `error` key. Callers gate on `isApiError(response)`, so any 2xx body without
        // `error` — `{}`, a proxy/CDN interstitial, a future shape change — would otherwise
        // read as "verified" and grant access to saved payments (JSAD-6922).
        //
        // The contract this asserts:
        //   success → 200 `{ success: true }`
        //   failure → 400 `{ success: false, error: <message> }`
        // The 400 arm rejects and is normalized in the catch below, so this only guards the
        // 2xx path.
        //
        // VERIFIED against the backend source (JustSaladLLC/OrderJustSalad-Apps @ bcac6f8e1,
        // 2026-08-13) — `OJS.App.Web.API/Controllers/API/v5/SecurityController.cs:97-118`:
        //   - `return Ok(new { success = true })` is the ONLY 200 exit; the other three are
        //     all `BadRequest`. The flag is a hardcoded literal, not a serialized field, so
        //     it cannot be conditionally omitted.
        //   - One route definition in the entire codebase (`HttpPost("verify-mfa")`), and no
        //     result filters / `IActionResultExecutor` in the API project, so nothing
        //     reshapes the body between controller and wire.
        //   - `git log -L 97,118` on that file returns a single commit (4a8f50d88, JSAD-5199,
        //     Mar 2026) and it shipped WITH the flag — there was never a `{}` shape. The
        //     pre-fix Cypress stub that returned `{}` was simply loose (a bare object was
        //     "not an error" to the old client), not a record of the real response.
        //
        // Re-verify with:
        //   git -C <backend> log -L 97,118:Source/Application/OJS.App.Web.API/Controllers/API/v5/SecurityController.cs
        //
        // Sibling MFA calls (resendMfa, startAccVerificationOtp, phone/verify) still infer
        // success from the ABSENCE of an error. That asymmetry is not evidence this endpoint
        // is wrong: the phone routes return `Ok(command)` and those command objects carry
        // their own `Success` property (`JustSalad.Core/Data/Security/Commands/
        // VerifyPhoneCommand.cs:33`), so the backend sends a flag on all of them — this is
        // just the only one the client reads. Tightening the others is a follow-up.
        if (data?.success !== true) {
            return { error: data?.error ?? 'Verification failed' } as ApiError;
        }

        return data;
    } catch (error) {
        if (axios.isAxiosError(error)) {
            // 401 = dead bearer. No meaningful backend copy, so synthesize `expired` and let
            // the modal prompt a re-sign-in instead of showing a generic error.
            if (error.response?.status === 401) {
                await reportMfa401WithStoredSession('verify-mfa');
                return { error: 'expired' } as ApiError;
            }
            if (error.response?.data?.error) {
                return error.response?.data as ApiError;
            }
            return handleAxiosError(error);
        }
        throw error;
    }
};

/**
 * A 401 on any blacklisted MFA route (`verify-mfa`, `resend-mfa`, `phone/*`) while a session
 * is STORED. On the login/signup 428 path nothing is in storage and the pre-MFA bearer simply
 * aging out is expected. A stored session means a signed-in user whose live session was
 * rejected — an app defect (a stale bearer copy, see useCheckoutAccountVerification) that is
 * otherwise invisible: the backend middleware 401s without logging and the UI only toasts.
 */
const reportMfa401WithStoredSession = async (route: string): Promise<void> => {
    if (await getStoredSession()) {
        Sentry.captureMessage('MFA 401 with a stored session', {
            level: 'error',
            extra: { route }
        });
    }
};

export type ResendMfaParams = {
    email: string;
    device_fingerprint: string;
    hct?: string;
};

const resendMfa = async (params: ResendMfaParams, authToken: string): Promise<void | ApiError> => {
    try {
        await client.post('v5/Security/resend-mfa', params, {
            headers: { Authorization: `Bearer ${authToken}` }
        });
    } catch (error) {
        if (axios.isAxiosError(error)) {
            // 401 = dead bearer. See verifyMfa.
            if (error.response?.status === 401) {
                await reportMfa401WithStoredSession('resend-mfa');
                return { error: 'expired' } as ApiError;
            }
            if (error.response?.data?.error) {
                return error.response?.data as ApiError;
            }
            return handleAxiosError(error);
        }
        throw error;
    }
};

/* ── Account verification (SMS OTP) ────────────────────────────────────────────
 * All endpoints take the session bearer; `/phone/skip` is empty-body, no hCaptcha;
 * all return 200 with no body. Each call short-circuits to ApiError({error:'captcha_missing'})
 * when `hct` is empty (defense against the captcha widget producing no token).
 * ──────────────────────────────────────────────────────────────────────────── */

export type StartAccVerificationOtpParams = {
    phone_number: string;
    hct: string;
    device_fingerprint: string;
    device_type: string;
    platform: string;
};

export type VerifyAccVerificationOtpParams = {
    code: string;
    hct: string;
    device_fingerprint: string;
    device_type: string;
    platform: string;
};

export type ResendAccVerificationOtpParams = {
    hct: string;
};

const mapAccVerificationHttpError = async (
    error: unknown,
    route: string,
    options?: { fourHundredCode?: string }
): Promise<ApiError> => {
    if (axios.isAxiosError(error)) {
        const status = error.response?.status;
        // 401 is special: the bearer is dead, so there's no meaningful backend copy to show —
        // resolve to the synthesized `expired` code so callers can prompt a re-sign-in.
        if (status === 401) {
            await reportMfa401WithStoredSession(route);
            return { error: 'expired' } as ApiError;
        }
        // 409 = the number belongs to another VERIFIED account. Callers branch on this to show
        // a dedicated modal naming the owning account, so BOTH the status and `masked_email`
        // have to survive the mapping — every arm below collapses to a bare message string and
        // would drop them.
        //
        // `error` here is for LOGGING ONLY: the 409 path shows the modal and no toast, so the
        // backend sentence is never rendered. It is kept (rather than dropped) so `isApiError`
        // still recognizes the result and Sentry/console breadcrumbs stay readable.
        if (status === 409) {
            const conflictMessage =
                error.response?.data?.error ?? error.response?.data?.errorMessage;
            const maskedEmail = error.response?.data?.masked_email;
            return {
                error:
                    typeof conflictMessage === 'string' && conflictMessage
                        ? conflictMessage
                        : 'phone_in_use',
                statusCode: 409,
                // Only surface a non-empty string: the modal's copy is built around this value,
                // and `''`/null would render "belongs to ." The modal falls back when absent.
                ...(typeof maskedEmail === 'string' && maskedEmail ? { maskedEmail } : {})
            } as PhoneInUseError;
        }
        // Prefer the backend's own message for everything else (including 429). This endpoint
        // surfaces it as either `error` or `errorMessage` (e.g. 400 → "Code expired or not
        // found", 429 → a rate-limit message); use whichever is present before falling back to
        // the generic per-call code.
        const backendMessage = error.response?.data?.error ?? error.response?.data?.errorMessage;
        if (typeof backendMessage === 'string' && backendMessage) {
            return { error: backendMessage } as ApiError;
        }
        if (status === 429) {
            return { error: 'Please wait before requesting another code.' } as ApiError;
        }
        if (status === 400 && options?.fourHundredCode) {
            return { error: options.fourHundredCode } as ApiError;
        }
        return handleAxiosError(error);
    }
    throw error;
};

const CAPTCHA_MISSING_ERROR: ApiError = { error: 'captcha_missing' } as ApiError;

const startAccVerificationOtp = async (
    params: StartAccVerificationOtpParams,
    sessionToken: string
): Promise<void | ApiError> => {
    if (!params.hct) {
        return CAPTCHA_MISSING_ERROR;
    }
    try {
        await client.post('v5/Security/phone/start', params, {
            headers: { Authorization: `Bearer ${sessionToken}` }
        });
    } catch (error) {
        return mapAccVerificationHttpError(error, 'phone/start', {
            fourHundredCode: 'Enter a valid phone number and try again.'
        });
    }
};

const verifyAccVerificationOtp = async (
    params: VerifyAccVerificationOtpParams,
    sessionToken: string
): Promise<void | ApiError> => {
    if (!params.hct) {
        return CAPTCHA_MISSING_ERROR;
    }
    try {
        await client.post('v5/Security/phone/verify', params, {
            headers: { Authorization: `Bearer ${sessionToken}` }
        });
    } catch (error) {
        // 400 → wrong/expired OTP. The mapper surfaces the backend's `errorMessage`
        // when present (e.g. "Code expired or not found"); this sentence is the
        // fallback only when the body carries no message.
        return mapAccVerificationHttpError(error, 'phone/verify', {
            fourHundredCode: 'That code is incorrect or expired. Please try again.'
        });
    }
};

const resendAccVerificationOtp = async (
    params: ResendAccVerificationOtpParams,
    sessionToken: string
): Promise<void | ApiError> => {
    if (!params.hct) {
        return CAPTCHA_MISSING_ERROR;
    }
    try {
        await client.post('v5/Security/phone/resend', params, {
            headers: { Authorization: `Bearer ${sessionToken}` }
        });
    } catch (error) {
        // 400 → closest user-facing semantic is rate-limit / cooldown.
        return mapAccVerificationHttpError(error, 'phone/resend', {
            fourHundredCode: 'Please wait before requesting another code.'
        });
    }
};

const skipAccVerificationOtp = async (sessionToken: string): Promise<void | ApiError> => {
    try {
        await client.post(
            'v5/Security/phone/skip',
            {},
            { headers: { Authorization: `Bearer ${sessionToken}` } }
        );
    } catch (error) {
        return mapAccVerificationHttpError(error, 'phone/skip');
    }
};

export default {
    login,
    signup,
    forgotPassword,
    resetPassword,
    refreshToken,
    exchangeOoboToken,
    exchangeSocialProviderToken,
    associateSocialProviderToken,
    verifyMfa,
    resendMfa,
    startAccVerificationOtp,
    verifyAccVerificationOtp,
    resendAccVerificationOtp,
    skipAccVerificationOtp
};
